import type { PartId, ProbeOutcome } from './types';
import { hashValue } from './hash';
import { stableStringify } from './hash';
import { ALL_PARTS } from './types';

/**
 * Assembles the fingerprint parts + overall fingerprint hash from probe results.
 *
 * Probes contribute raw, already-normalized `parts` data. Hashing is centralized
 * here so every part is produced identically.
 */
export async function assembleParts(
  outcomes: ProbeOutcome[],
): Promise<{ parts: Partial<Record<PartId, string>>; fingerprintHash: string }> {
  // Merge part inputs from all probes. A part is only hashed if at least one
  // probe contributed data for it, so a missing probe leaves the part undefined
  // rather than hashing "undefined" into a stable value.
  const inputs = new Map<PartId, Record<string, unknown>>();

  for (const outcome of outcomes) {
    const partData = (outcome as ProbeOutcome & { partInputs?: Partial<Record<PartId, unknown>> })
      .partInputs;
    if (!partData) continue;
    for (const [part, value] of Object.entries(partData) as [PartId, unknown][]) {
      if (value === undefined) continue;
      const bucket = inputs.get(part) ?? {};
      bucket[outcome.probe.id] = value;
      inputs.set(part, bucket);
    }
  }

  const parts: Partial<Record<PartId, string>> = {};
  for (const part of ALL_PARTS) {
    const input = inputs.get(part);
    if (!input || Object.keys(input).length === 0) continue;
    parts[part] = await hashValue(input);
  }

  // fingerprintHash summarizes the whole device: hash the ordered part digests.
  const ordered = ALL_PARTS.filter((p) => parts[p]).map((p) => `${p}=${parts[p]}`);
  const fingerprintHash = ordered.length ? await hashValue(ordered.join('|')) : '';

  return { parts, fingerprintHash };
}

/** Stable, normalized string of a part input — exported for tests / compare. */
export function partInputString(input: unknown): string {
  return stableStringify(input);
}
